import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { contractorsApi } from '../../api/client';
import { Card, CardHeader, CardTitle, CardBody } from '../../components/ui/Card';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../components/ui/Table';
import Button from '../../components/ui/Button';
import Input, { Textarea, Label } from '../../components/ui/Input';
import Badge from '../../components/ui/Badge';
import Modal from '../../components/ui/Modal';
import { Plus, Edit, Trash2, Search, Check, X, HardHat, ShieldCheck, Ban, CircleCheck } from 'lucide-react';

export default function ContractorsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [contractorModal, setContractorModal] = useState({ open: false, contractor: null });
  const [deleteModal, setDeleteModal] = useState({ open: false, contractor: null });

  const [formData, setFormData] = useState({
    name: '',
    shortName: '',
    contactPerson: '',
    phone: '',
    email: '',
    address: '',
    siteManager: '',
    siteManagerPhone: '',
    safetyOfficer: '',
    safetyOfficerPhone: '',
    isActive: true,
  });

  // Fetch contractors
  const { data: contractors, isLoading } = useQuery({
    queryKey: ['contractors', search],
    queryFn: () => contractorsApi.getAll({ search }),
    select: (res) => res.data,
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data) => contractorsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['contractors']);
      setContractorModal({ open: false, contractor: null });
      resetForm();
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => contractorsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['contractors']);
      setContractorModal({ open: false, contractor: null });
      resetForm();
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id) => contractorsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['contractors']);
      setDeleteModal({ open: false, contractor: null });
    },
  });

  // 停用 / 啟用
  const activeMutation = useMutation({
    mutationFn: ({ id, isActive }) => contractorsApi.setActive(id, isActive),
    onSuccess: () => queryClient.invalidateQueries(['contractors']),
  });

  // Records affected by deleting the selected contractor
  const { data: deleteUsage } = useQuery({
    queryKey: ['contractor-usage', deleteModal.contractor?.id],
    queryFn: () => contractorsApi.getUsage(deleteModal.contractor.id),
    select: (res) => res.data,
    enabled: deleteModal.open && Boolean(deleteModal.contractor?.id),
  });

  const resetForm = () => {
    setFormData({
      name: '',
      shortName: '',
      contactPerson: '',
      phone: '',
      email: '',
      address: '',
      siteManager: '',
      siteManagerPhone: '',
      safetyOfficer: '',
      safetyOfficerPhone: '',
      isActive: true,
    });
  };

  const openCreateModal = () => {
    resetForm();
    setContractorModal({ open: true, contractor: null });
  };

  const openEditModal = (contractor) => {
    setFormData({
      name: contractor.name,
      shortName: contractor.shortName || '',
      contactPerson: contractor.contactPerson || '',
      phone: contractor.phone || '',
      email: contractor.email || '',
      address: contractor.address || '',
      siteManager: contractor.siteManager || '',
      siteManagerPhone: contractor.siteManagerPhone || '',
      safetyOfficer: contractor.safetyOfficer || '',
      safetyOfficerPhone: contractor.safetyOfficerPhone || '',
      isActive: Boolean(contractor.isActive),
    });
    setContractorModal({ open: true, contractor });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (contractorModal.contractor) {
      updateMutation.mutate({ id: contractorModal.contractor.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">
          {t('contractors.title', '承攬商管理')}
        </h1>
        <Button onClick={openCreateModal}>
          <Plus className="w-4 h-4 mr-2" />
          {t('contractors.new', '新增承攬商')}
        </Button>
      </div>

      {/* Search */}
      <Card>
        <CardBody>
          <div className="relative max-w-md">
            <Input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('common.search')}
              className="pl-9"
            />
            <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
          </div>
        </CardBody>
      </Card>

      {/* Contractors Table */}
      <Card>
        <CardBody className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-tpc-blue"></div>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('contractors.name', '公司名稱')}</TableHead>
                  <TableHead>{t('contractors.shortName', '簡稱')}</TableHead>
                  <TableHead>
                    <div className="flex items-center gap-1">
                      <HardHat className="w-4 h-4" />
                      {t('contractors.siteManager', '工地負責人')}
                    </div>
                  </TableHead>
                  <TableHead>
                    <div className="flex items-center gap-1">
                      <ShieldCheck className="w-4 h-4" />
                      {t('contractors.safetyOfficer', '職安人員')}
                    </div>
                  </TableHead>
                  <TableHead>{t('common.status')}</TableHead>
                  <TableHead className="text-right">{t('common.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {contractors?.length > 0 ? (
                  contractors.map((contractor) => (
                    <TableRow key={contractor.id}>
                      <TableCell className="font-medium">{contractor.name}</TableCell>
                      <TableCell>{contractor.shortName || '-'}</TableCell>
                      <TableCell>
                        {contractor.siteManager ? (
                          <div>
                            <div className="font-medium">{contractor.siteManager}</div>
                            {contractor.siteManagerPhone && (
                              <div className="text-[0.85em] text-gray-500">{contractor.siteManagerPhone}</div>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {contractor.safetyOfficer ? (
                          <div>
                            <div className="font-medium">{contractor.safetyOfficer}</div>
                            {contractor.safetyOfficerPhone && (
                              <div className="text-[0.85em] text-gray-500">{contractor.safetyOfficerPhone}</div>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {contractor.isActive ? (
                          <Badge variant="success">
                            <Check className="w-3 h-3 mr-1" />
                            {t('users.active')}
                          </Badge>
                        ) : (
                          <Badge variant="danger">
                            <X className="w-3 h-3 mr-1" />
                            {t('users.inactive')}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openEditModal(contractor)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            title={contractor.isActive ? t('users.deactivate', '停用') : t('users.activate', '啟用')}
                            onClick={() => activeMutation.mutate({ id: contractor.id, isActive: !contractor.isActive })}
                          >
                            {contractor.isActive
                              ? <Ban className="w-4 h-4 text-amber-600" />
                              : <CircleCheck className="w-4 h-4 text-green-600" />}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            title={t('common.delete')}
                            onClick={() => setDeleteModal({ open: true, contractor })}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-gray-500 py-12">
                      {t('common.noData')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardBody>
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={contractorModal.open}
        onClose={() => setContractorModal({ open: false, contractor: null })}
        title={contractorModal.contractor ? t('contractors.edit', '編輯承攬商') : t('contractors.new', '新增承攬商')}
        size="lg"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label required>{t('contractors.name', '公司名稱')}</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
              />
            </div>
            <div>
              <Label>{t('contractors.shortName', '簡稱')}</Label>
              <Input
                value={formData.shortName}
                onChange={(e) => setFormData((prev) => ({ ...prev, shortName: e.target.value }))}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('contractors.contactPerson', '聯絡人')}</Label>
              <Input
                value={formData.contactPerson}
                onChange={(e) => setFormData((prev) => ({ ...prev, contactPerson: e.target.value }))}
              />
            </div>
            <div>
              <Label>{t('contractors.phone', '電話')}</Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData((prev) => ({ ...prev, phone: e.target.value }))}
              />
            </div>
          </div>

          <div>
            <Label>{t('contractors.email', '電子郵件')}</Label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
            />
          </div>

          {/* Site Manager & Safety Officer Section */}
          <div className="border-t pt-4 mt-4">
            <h4 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
              <HardHat className="w-4 h-4" />
              {t('contractors.siteManager', '工地負責人')}
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('inspectors.name', '姓名')}</Label>
                <Input
                  value={formData.siteManager}
                  onChange={(e) => setFormData((prev) => ({ ...prev, siteManager: e.target.value }))}
                  placeholder={t('inspectors.namePlaceholder', '例如：陳XX')}
                />
              </div>
              <div>
                <Label>{t('inspectors.phone', '電話')}</Label>
                <Input
                  value={formData.siteManagerPhone}
                  onChange={(e) => setFormData((prev) => ({ ...prev, siteManagerPhone: e.target.value }))}
                  placeholder="0912-345678"
                />
              </div>
            </div>
          </div>

          <div className="border-t pt-4">
            <h4 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
              <ShieldCheck className="w-4 h-4" />
              {t('contractors.safetyOfficer', '職安人員')}
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('inspectors.name', '姓名')}</Label>
                <Input
                  value={formData.safetyOfficer}
                  onChange={(e) => setFormData((prev) => ({ ...prev, safetyOfficer: e.target.value }))}
                  placeholder={t('inspectors.namePlaceholder', '例如：陳XX')}
                />
              </div>
              <div>
                <Label>{t('inspectors.phone', '電話')}</Label>
                <Input
                  value={formData.safetyOfficerPhone}
                  onChange={(e) => setFormData((prev) => ({ ...prev, safetyOfficerPhone: e.target.value }))}
                  placeholder="0912-345678"
                />
              </div>
            </div>
          </div>

          <div>
            <Label>{t('contractors.address', '地址')}</Label>
            <Textarea
              value={formData.address}
              onChange={(e) => setFormData((prev) => ({ ...prev, address: e.target.value }))}
              rows={2}
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setContractorModal({ open: false, contractor: null })}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              isLoading={createMutation.isPending || updateMutation.isPending}
            >
              {t('common.save')}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Modal */}
      <Modal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, contractor: null })}
        title={t('common.confirm')}
        size="sm"
      >
        <div className="space-y-4">
          <p>{t('contractors.deleteConfirm', '確定要永久刪除此承攬商嗎？')}</p>
          <p className="text-gray-500">{deleteModal.contractor?.name}</p>
          {deleteUsage && (deleteUsage.workItems > 0 || deleteUsage.personnel > 0 || deleteUsage.users > 0) ? (
            <div className="text-sm text-red-600 space-y-1">
              <p>{t('contractors.deleteImpact', '刪除後無法復原，並會影響：')}</p>
              <ul className="list-disc list-inside">
                {deleteUsage.workItems > 0 && <li>{t('contractors.impactWorkItems', '{{count}} 筆工作項目的承攬商將變為空白', { count: deleteUsage.workItems })}</li>}
                {deleteUsage.personnel > 0 && <li>{t('contractors.impactPersonnel', '{{count}} 位工地負責人/職安人員資料將一併刪除', { count: deleteUsage.personnel })}</li>}
                {deleteUsage.users > 0 && <li>{t('contractors.impactUsers', '{{count}} 個承攬商帳號將被停用', { count: deleteUsage.users })}</li>}
              </ul>
              <p className="text-gray-500">{t('contractors.deactivateInstead', '若只是不再合作，建議改用「停用」以保留歷史紀錄。')}</p>
            </div>
          ) : (
            <p className="text-sm text-gray-500">{t('contractors.deleteUnused', '此承攬商尚無任何施工紀錄，可安全刪除。')}</p>
          )}
          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => setDeleteModal({ open: false, contractor: null })}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              isLoading={deleteMutation.isPending}
              onClick={() => deleteMutation.mutate(deleteModal.contractor?.id)}
            >
              {t('common.delete')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
